"use client";

import React from "react";
import { Calendar, Clock, DollarSign, User, Phone, X } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import type { Field } from "@/lib/types/booking";
import type { InquiryFormData } from "./types";
import type { Pricing } from "@/lib/types/booking";

interface SummaryModalProps {
  summaryData: InquiryFormData;
  pricing: Pricing;
  allFields: Field[];
  onClose: () => void;
  onConfirm: () => void;
}

export function SummaryModal({
  summaryData,
  pricing,
  allFields,
  onClose,
  onConfirm,
}: SummaryModalProps) {
  const { t } = useTranslation();

  const fieldName =
    allFields.find((f) => String(f.fieldId) === summaryData.cancha)
      ?.fieldName ?? summaryData.cancha;

  return (
    <motion.div
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      exit={{opacity: 0}}
      transition={{duration: 0.2}}
      className="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto bg-slate-900/60 p-2 pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))] backdrop-blur-sm sm:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{opacity: 0, scale: 0.96, y: 8}}
        animate={{opacity: 1, scale: 1, y: 0}}
        exit={{opacity: 0, scale: 0.96, y: 8}}
        transition={{type: "spring", duration: 0.4}}
        onClick={(e) => e.stopPropagation()}
        className="mx-2 flex max-h-[calc(100dvh-1rem)] min-h-0 w-full max-w-md min-w-0 flex-col overflow-hidden rounded-2xl bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] sm:max-h-[calc(100dvh-2rem)] sm:mx-4"
      >
        <div className="flex shrink-0 min-w-0 items-center justify-between border-b border-slate-100 px-4 py-4 sm:px-6 sm:py-5">
          <h3 className="text-lg font-semibold tracking-tight text-slate-900">
            {t("summary.title")}
          </h3>
          <button
            onClick={onClose}
            className="flex h-11 w-11 min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label={t("modal.close")}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-1 overflow-y-auto overflow-x-hidden overscroll-contain px-4 py-4 sm:px-6 sm:py-5">
          <SummaryRow icon={<Calendar className="h-5 w-5" />} label={t("summary.fecha")} value={summaryData.fecha} variant="primary" />
          <SummaryRow
            icon={<Clock className="h-5 w-5" />}
            label={t("summary.horario")}
            value={`${summaryData.horario} – ${summaryData.horarioHasta}`}
            variant="primary"
          />
          <SummaryRow icon={<span className="text-lg">⚽</span>} label={t("summary.cancha")} value={fieldName} variant="primary" />
          <SummaryRow icon={<User className="h-5 w-5" />} label={t("summary.nombre")} value={summaryData.nombre} />
          <SummaryRow icon={<Phone className="h-5 w-5" />} label={t("summary.telefono")} value={summaryData.telefono} />

          <div className="rounded-xl border-2 border-primary/20 bg-primary-soft/50 px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-white">
                  <DollarSign className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-primary">
                    {t("summary.totalToPay")}
                  </p>
                  <p className="text-2xl font-bold text-primary-strong">
                    ${pricing.totalAmount.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="font-medium">{t("summary.warning1")}</p>
            <p className="mt-1 font-semibold">{t("summary.warning2")}</p>
            <p className="mt-1 text-amber-800">{t("summary.warning3")}</p>
          </div>
        </div>

        <div className="flex shrink-0 min-w-0 flex-wrap gap-3 border-t border-slate-100 bg-slate-50/50 px-4 py-4 sm:px-6">
          <button
            onClick={onClose}
            className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 hover:border-slate-300"
          >
            {t("summary.volver")}
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-primary-strong"
          >
            {t("summary.payReservation")}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function SummaryRow({
  icon,
  label,
  value,
  variant = "default",
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  variant?: "primary" | "default";
}) {
  return (
    <div className="flex items-center gap-4 rounded-xl bg-slate-50/80 px-4 py-3">
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg [&>span]:flex [&>span]:items-center [&>span]:justify-center ${
        variant === "primary" ? "bg-primary/10 text-primary" : "bg-slate-200/80 text-slate-600"
      }`}>
        {icon}
      </div>
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
          {label}
        </p>
        <p className="font-medium text-slate-900">{value}</p>
      </div>
    </div>
  );
}
